import type { Card, GameState, HandState } from '../game/types';
import type { EnemyAiDecision, EnemyAiDecisionContext } from './types';

const TITLES = {
  sigil: 'blackhand sigil',
  momentum: 'momentum',
  eviscerate: 'eviscerate',
  execute: 'execute',
  pressurePoint: 'pressure point',
  shadowstep: 'shadowstep',
} as const;

const ATTACK_TITLES = [TITLES.execute, TITLES.eviscerate] as const;
const SETUP_TITLES = [TITLES.sigil, TITLES.shadowstep, TITLES.pressurePoint, TITLES.momentum] as const;

export const BLACKHAND_AI_TUNING: Record<string, number> = {
  lethalExecute: 20000,
  lethalEviscerate: 19500,
  markUnmarked: 10400,
  shadowstepComboLead: 9300,
  eviscerateAfterFreshMark: 9300,
  executeOnEstablishedMark: 7600,
  eviscerateOnEstablishedMark: 8350,
  pressurePointPunish: 9000,
  shadowstepCover: 8700,
  momentumSetup: 8450,
  fallbackExecute: 1800,
  fallbackEviscerate: 2100,
  fallbackShadowstep: 2600,
  fallbackPressurePoint: 2550,
  fallbackMomentum: 2350,
  hardNo: -100000,
  softRepeatPenalty: 1800,
  strongRepeatPenalty: 3200,
  payoffAlreadyUsedPenalty: 7000,
  executeWithoutEstablishedMarkPenalty: 2600,
  executeFreshMarkPenalty: 5000,
  momentumWhileAlreadyAcceleratedPenalty: 4200,
  executeRepeatCooldownPenalty: 100000,
  consecutiveAttackPenalty: 2600,
  establishedAttackPenalty: 950,
  eviscerateRepeatCooldownPenalty: 100000,
  tripleAttackBlockPenalty: 100000,
};

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function handCount(hand: HandState | null | undefined): number {
  if (!hand) return 0;
  let count = 0;
  if (hand.main?.physical) count += 1;
  if (hand.main?.magic) count += 1;
  count += Array.isArray(hand.extra) ? hand.extra.length : 0;
  return count;
}

function titleOf(card: Card | null | undefined): string {
  return norm(card?.title ?? (card as any)?.name ?? card?.id);
}

function cardByTitle(cards: Card[], title: string): Card | null {
  const wanted = norm(title);
  return cards.find((card) => titleOf(card) === wanted) ?? null;
}

function isAttackTitle(title: string): boolean {
  return ATTACK_TITLES.includes(title as (typeof ATTACK_TITLES)[number]);
}

function isSetupTitle(title: string): boolean {
  return SETUP_TITLES.includes(title as (typeof SETUP_TITLES)[number]);
}

function getEnemyHistory(state: GameState): { currentTurnActions: string[]; lastTurnActions: string[] } {
  const rawCurrent = Array.isArray((state as any).enemyAiCurrentTurnActions)
    ? ((state as any).enemyAiCurrentTurnActions as string[]).map(norm).filter(Boolean)
    : [];
  const rawLast = Array.isArray((state as any).enemyAiLastTurnActions)
    ? ((state as any).enemyAiLastTurnActions as string[]).map(norm).filter(Boolean)
    : [];

  if (state.phase === 'enemy') {
    return { currentTurnActions: rawCurrent, lastTurnActions: rawLast };
  }

  return {
    currentTurnActions: [],
    lastTurnActions: rawCurrent.length > 0 ? rawCurrent : rawLast,
  };
}

function getRecentEnemyActionHistory(state: GameState): string[] {
  const { currentTurnActions, lastTurnActions } = getEnemyHistory(state);
  return [...lastTurnActions, ...currentTurnActions].map(norm).filter(Boolean);
}

function getTrailingAttackCount(actionHistory: string[]): number {
  let count = 0;
  for (let i = actionHistory.length - 1; i >= 0; i -= 1) {
    if (!isAttackTitle(actionHistory[i] ?? '')) break;
    count += 1;
  }
  return count;
}

function playerMarked(state: GameState): boolean {
  return !!((state as any).playerMarkedByBlackhand);
}

function markPredatesCurrentTurn(state: GameState): boolean {
  if (!playerMarked(state)) return false;
  const appliedTurn = Number((state as any).playerMarkedTurn ?? -1);
  const currentTurn = Number((state as any).turn ?? 0);
  return appliedTurn >= 0 && appliedTurn < currentTurn;
}

function markAppliedThisTurn(state: GameState): boolean {
  if (!playerMarked(state)) return false;
  const appliedTurn = Number((state as any).playerMarkedTurn ?? -1);
  const currentTurn = Number((state as any).turn ?? 0);
  return appliedTurn === currentTurn;
}

function expectedStrongPlayerResponse(state: GameState): boolean {
  const sp = Math.max(0, Number(state.player?.sp ?? 0) || 0);
  const cardsInHand = handCount(state.hand);
  const lastPlays = Math.max(0, Number((state as any).lastPlayerTurnPlays ?? 0) || 0);
  const lastFocusedOnly = !!((state as any).lastPlayerTurnFocusedOnly);
  return sp >= 30 || cardsInHand >= 3 || lastPlays >= 3 || lastFocusedOnly;
}

function playerOverextendedChain(state: GameState): boolean {
  const lastOverload = Math.max(0, Number((state as any).lastPlayerTurnMagicChainOverloadSp ?? 0) || 0);
  const lastPlays = Math.max(0, Number((state as any).lastPlayerTurnPlays ?? 0) || 0);
  return lastOverload >= 3 || lastPlays >= 4;
}

function playerLikelyJustClearedMark(state: GameState): boolean {
  return !playerMarked(state) && !!((state as any).lastPlayerTurnFocusedOnly);
}

function shouldSkipImmediateRemark(state: GameState): boolean {
  if (!playerLikelyJustClearedMark(state)) return false;
  const currentTurn = Math.max(0, Number((state as any).turn ?? 0) || 0);
  const hpBucket = Math.floor(Math.max(0, Number(state.player?.hp ?? 0) || 0) / 10);
  const spBucket = Math.floor(Math.max(0, Number(state.player?.sp ?? 0) || 0) / 10);
  return ((currentTurn + hpBucket + spBucket) % 3) === 0;
}

function shouldPrepareBigNextTurn(state: GameState, lastTurnActions: string[]): boolean {
  const lastFocusedOnly = !!((state as any).lastPlayerTurnFocusedOnly);
  const markedBefore = markPredatesCurrentTurn(state);
  const lastOverload = Math.max(0, Number((state as any).lastPlayerTurnMagicChainOverloadSp ?? 0) || 0);
  const playerHp = Math.max(0, Number(state.player?.hp ?? 0) || 0);
  const alreadyAccelerated = Math.max(0, Number((state as any).enemyBonusActionsNextTurn ?? 0) || 0) > 0;
  const usedMomentumLastTurn = lastTurnActions.includes(TITLES.momentum);
  if (alreadyAccelerated || usedMomentumLastTurn) return false;
  return lastFocusedOnly || markedBefore || lastOverload >= 3 || playerHp <= 80;
}

function executeWouldBeLethal(state: GameState): boolean {
  const damage = markPredatesCurrentTurn(state) ? 100 : 50;
  const hp = Math.max(0, Number(state.player?.hp ?? 0) || 0);
  return hp <= damage;
}

function eviscerateWouldBeLethal(state: GameState): boolean {
  const hp = Math.max(0, Number(state.player?.hp ?? 0) || 0);
  const armor = Math.max(0, Number(state.player?.armor ?? 0) || 0);
  return hp + armor <= 40;
}

function scoreCard(card: Card, context: EnemyAiDecisionContext): { score: number; reason: string } {
  const { state } = context;
  const { currentTurnActions, lastTurnActions } = getEnemyHistory(state);
  const currentTitle = titleOf(card);
  const currentActionsRemaining = Math.max(1, Number((state as any).enemyActionsRemainingThisTurn ?? 1) || 1);
  const firstAction = currentTurnActions[0] ?? '';
  const payoffAlreadyUsedThisTurn = currentTurnActions.some((title) => title === TITLES.execute || title === TITLES.eviscerate);
  const unmarked = !playerMarked(state);
  const markedThisTurn = markAppliedThisTurn(state);
  const markedBefore = markPredatesCurrentTurn(state);
  const strongResponseLikely = expectedStrongPlayerResponse(state);
  const chainGreedLikely = playerOverextendedChain(state);
  const shouldBankTempo = shouldPrepareBigNextTurn(state, lastTurnActions);
  const justClearedMark = playerLikelyJustClearedMark(state);
  const skipImmediateRemark = shouldSkipImmediateRemark(state);
  const usedPressureLastTurn = lastTurnActions.includes(TITLES.pressurePoint);
  const usedShadowstepLastTurn = lastTurnActions.includes(TITLES.shadowstep);
  const usedMomentumLastTurn = lastTurnActions.includes(TITLES.momentum);
  const usedExecuteLastTurn = lastTurnActions.includes(TITLES.execute);
  const usedEviscerateLastTurn = lastTurnActions.includes(TITLES.eviscerate);
  const playerArmor = Math.max(0, Number(state.player?.armor ?? 0) || 0);
  const playerReflect = Math.max(0, Number(state.player?.reflectPct ?? 0) || 0);
  const playerSp = Math.max(0, Number(state.player?.sp ?? 0) || 0);
  const playerHp = Math.max(0, Number(state.player?.hp ?? 0) || 0);
  const actionHistory = getRecentEnemyActionHistory(state);
  const trailingAttackCount = getTrailingAttackCount(actionHistory);
  const lastEnemyAction = actionHistory[actionHistory.length - 1] ?? '';
  const setupUsedRecently = actionHistory.slice(-2).some(isSetupTitle);

  if (currentTitle === TITLES.execute) {
    if (usedExecuteLastTurn) {
      return {
        score: BLACKHAND_AI_TUNING.hardNo - BLACKHAND_AI_TUNING.executeRepeatCooldownPenalty,
        reason: 'execute_cooldown_last_turn',
      };
    }
    if (trailingAttackCount >= 2) {
      return {
        score: BLACKHAND_AI_TUNING.hardNo - BLACKHAND_AI_TUNING.tripleAttackBlockPenalty,
        reason: 'avoid_three_attacks_in_a_row',
      };
    }
    if (executeWouldBeLethal(state)) {
      return { score: BLACKHAND_AI_TUNING.lethalExecute + (markedBefore ? 250 : 0), reason: 'lethal_execute' };
    }
    if (payoffAlreadyUsedThisTurn) {
      return { score: BLACKHAND_AI_TUNING.fallbackExecute - BLACKHAND_AI_TUNING.payoffAlreadyUsedPenalty, reason: 'avoid_double_payoff' };
    }
    if (markedBefore) {
      let score: number = BLACKHAND_AI_TUNING.executeOnEstablishedMark;
      score += Math.min(2600, playerArmor * 22);
      score += Math.max(0, 85 - playerHp) * 6;
      if (playerReflect >= 20) score -= 900;
      if (playerSp >= 30) score -= 650;
      if (firstAction === TITLES.shadowstep) score += 320;
      if (firstAction === TITLES.pressurePoint) score += 160;
      if (trailingAttackCount >= 1) score -= BLACKHAND_AI_TUNING.consecutiveAttackPenalty;
      if (!setupUsedRecently) score -= BLACKHAND_AI_TUNING.establishedAttackPenalty;
      return { score, reason: 'established_mark_execute' };
    }
    if (markedThisTurn) {
      return {
        score: BLACKHAND_AI_TUNING.fallbackExecute - BLACKHAND_AI_TUNING.executeFreshMarkPenalty,
        reason: 'fresh_mark_execute_is_rare',
      };
    }
    return {
      score:
        BLACKHAND_AI_TUNING.fallbackExecute -
        BLACKHAND_AI_TUNING.executeWithoutEstablishedMarkPenalty +
        Math.min(400, playerArmor * 6) -
        (trailingAttackCount >= 1 ? BLACKHAND_AI_TUNING.consecutiveAttackPenalty : 0),
      reason: 'fallback_execute',
    };
  }

  if (currentTitle === TITLES.eviscerate) {
    if (lastEnemyAction === TITLES.eviscerate || usedEviscerateLastTurn) {
      return {
        score: BLACKHAND_AI_TUNING.hardNo - BLACKHAND_AI_TUNING.eviscerateRepeatCooldownPenalty,
        reason: 'eviscerate_cooldown_last_action',
      };
    }
    if (trailingAttackCount >= 2) {
      return {
        score: BLACKHAND_AI_TUNING.hardNo - BLACKHAND_AI_TUNING.tripleAttackBlockPenalty,
        reason: 'avoid_three_attacks_in_a_row',
      };
    }
    if (eviscerateWouldBeLethal(state)) {
      return { score: BLACKHAND_AI_TUNING.lethalEviscerate + Math.min(500, playerReflect * 2), reason: 'lethal_eviscerate' };
    }
    if (payoffAlreadyUsedThisTurn) {
      return { score: BLACKHAND_AI_TUNING.fallbackEviscerate - BLACKHAND_AI_TUNING.payoffAlreadyUsedPenalty, reason: 'avoid_double_payoff' };
    }
    if (markedThisTurn) {
      let score: number = BLACKHAND_AI_TUNING.eviscerateAfterFreshMark;
      score += Math.min(1800, playerReflect * 10);
      score += Math.min(1200, playerSp * 6);
      if (firstAction === TITLES.sigil) score += 700;
      if (trailingAttackCount >= 1) score -= BLACKHAND_AI_TUNING.consecutiveAttackPenalty;
      return { score, reason: 'fresh_mark_eviscerate' };
    }
    if (markedBefore) {
      let score: number = BLACKHAND_AI_TUNING.eviscerateOnEstablishedMark;
      score += Math.min(2800, playerReflect * 16);
      score += Math.min(2100, playerSp * 9);
      if (playerArmor >= 40) score -= 500;
      if (firstAction === TITLES.pressurePoint) score += 420;
      if (firstAction === TITLES.shadowstep) score += 220;
      if (usedExecuteLastTurn) score += 900;
      if (trailingAttackCount >= 1) score -= BLACKHAND_AI_TUNING.consecutiveAttackPenalty;
      if (!setupUsedRecently) score -= 450;
      return { score, reason: 'marked_eviscerate' };
    }
    return {
      score:
        BLACKHAND_AI_TUNING.fallbackEviscerate +
        Math.min(1200, playerReflect * 10) +
        Math.min(700, playerSp * 3) -
        (trailingAttackCount >= 1 ? BLACKHAND_AI_TUNING.consecutiveAttackPenalty : 0),
      reason: 'fallback_eviscerate',
    };
  }

  if (currentTitle === TITLES.sigil) {
    if (!unmarked) {
      return { score: BLACKHAND_AI_TUNING.hardNo, reason: 'already_marked' };
    }
    if (skipImmediateRemark) {
      return { score: BLACKHAND_AI_TUNING.hardNo + 250, reason: 'delay_reapply_mark_after_focus_break' };
    }
    let score: number = BLACKHAND_AI_TUNING.markUnmarked;
    if (firstAction === TITLES.momentum) score += 850;
    if (currentTurnActions.includes(TITLES.momentum)) score += 500;
    if (currentActionsRemaining > 1 && currentTurnActions.length === 0) score += 250;
    if (shouldBankTempo && cardByTitle(context.availableCards, TITLES.eviscerate)) score += 180;
    if (trailingAttackCount >= 1) score += 900;
    if (justClearedMark) score -= 450;
    return { score, reason: firstAction === TITLES.momentum ? 'momentum_then_mark' : 'mark_unmarked_player' };
  }

  if (currentTitle === TITLES.shadowstep) {
    if (currentTurnActions.includes(TITLES.shadowstep)) {
      return { score: BLACKHAND_AI_TUNING.hardNo, reason: 'shadowstep_already_used_this_turn' };
    }
    if (lastEnemyAction === TITLES.shadowstep || usedShadowstepLastTurn) {
      return { score: BLACKHAND_AI_TUNING.hardNo + 100, reason: 'shadowstep_repeat_blocked' };
    }
    let score: number = BLACKHAND_AI_TUNING.fallbackShadowstep;
    if (markedThisTurn) score = BLACKHAND_AI_TUNING.shadowstepCover + 350;
    if (markedBefore) score = BLACKHAND_AI_TUNING.shadowstepCover + 520;
    if (strongResponseLikely) score += 900;
    if (currentActionsRemaining > 1 && currentTurnActions.length === 0 && markedBefore && strongResponseLikely) {
      score = BLACKHAND_AI_TUNING.shadowstepComboLead + 700;
    }
    if (firstAction === TITLES.sigil) score += 180;
    if (trailingAttackCount >= 1) score += 700;
    if (justClearedMark) score -= 500;
    return { score, reason: markedBefore ? 'cover_established_window' : 'shadowstep_cover' };
  }

  if (currentTitle === TITLES.pressurePoint) {
    if (currentTurnActions.includes(TITLES.pressurePoint)) {
      return { score: BLACKHAND_AI_TUNING.hardNo, reason: 'pressure_point_already_used_this_turn' };
    }
    let score: number = BLACKHAND_AI_TUNING.fallbackPressurePoint;
    if (usedPressureLastTurn) score -= BLACKHAND_AI_TUNING.softRepeatPenalty;
    if (chainGreedLikely) score = BLACKHAND_AI_TUNING.pressurePointPunish + 1000;
    if (markedBefore || markedThisTurn) score += 650;
    if (firstAction === TITLES.sigil) score += 120;
    if (trailingAttackCount >= 1) score += 760;
    if (justClearedMark) score += 650;
    return { score, reason: chainGreedLikely ? 'punish_chain_greed' : 'pressure_point_control' };
  }

  if (currentTitle === TITLES.momentum) {
    if (currentTurnActions.includes(TITLES.momentum)) {
      return { score: BLACKHAND_AI_TUNING.hardNo, reason: 'momentum_already_used_this_turn' };
    }
    let score: number = BLACKHAND_AI_TUNING.fallbackMomentum;
    if (usedMomentumLastTurn) score -= BLACKHAND_AI_TUNING.strongRepeatPenalty;
    if (Math.max(0, Number((state as any).enemyBonusActionsNextTurn ?? 0) || 0) > 0) {
      score -= BLACKHAND_AI_TUNING.momentumWhileAlreadyAcceleratedPenalty;
    }
    if (currentActionsRemaining > 1) score -= 700;
    if (shouldBankTempo) score = BLACKHAND_AI_TUNING.momentumSetup + (markedBefore ? 300 : 0);
    if (unmarked && !cardByTitle(context.availableCards, TITLES.sigil)) score += 500;
    if (trailingAttackCount >= 1) score += 850;
    if (justClearedMark) score += 900;
    return { score, reason: shouldBankTempo ? 'bank_next_turn_tempo' : 'fallback_momentum' };
  }

  return { score: BLACKHAND_AI_TUNING.hardNo, reason: 'unsupported_card' };
}

export function pickBlackhandCard(context: EnemyAiDecisionContext): EnemyAiDecision {
  if (!Array.isArray(context.availableCards) || context.availableCards.length === 0) {
    return { card: null, reason: 'no_available_cards' };
  }

  const scored = context.availableCards.map((card) => {
    const { score, reason } = scoreCard(card, context);
    return { card, score, reason, title: String(card.title ?? card.id ?? '') };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.title.localeCompare(b.title);
  });

  const top = scored[0] ?? null;
  return {
    card: top?.card ?? null,
    reason: top?.reason,
    debug: scored.map((entry) => ({ title: entry.title, score: entry.score, reason: entry.reason })),
  };
}
